import SearchIcon from '@mui/icons-material/Search';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
    Alert,
    Avatar,
    AvatarGroup,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Drawer,
    Grid,
    IconButton,
    Input,
    Menu,
    MenuItem,
    Skeleton,
    Select,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
    alpha,
} from '@mui/material';
import { getSignedUrl, uploadFile } from 'api';
import { getPersonalRecords, getTrackEvents } from 'api/TrackEvents';
import {
    AvatarWithInitials,
    confirmViaDialog,
    CustomButton,
    NextPrevPagination,
    RowDivider,
    SnackBar,
    ThumbnailTitleChip,
    TopBarWithShareIconLayout,
    UploadLogo,
} from 'components';
import { AbilityContext, Can } from 'components/Functional/Can';
import debounce from 'lodash/debounce';
import Lottie from 'lottie-react';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { pushMessage } from 'redux/reducers/messages';
import { primarySportSelector, getOrganizationsWithOrgAdminRole } from 'redux/reducers/user';
import { useAppDispatch } from 'redux/store';
import ROUTES from 'Routes/routes';
import { NumberParam, StringParam, useQueryParams } from 'use-query-params';
import {
    getIdFromEndOfRoute,
    Group,
    Individual,
    Member,
    MEMBER_TYPES,
    MemberType,
    Meta,
    PaginationLink,
    useToggle,
    formatTrackEventResult,
    normalizeTrackEventName,
} from 'utils';
import Archive from '../../../lottie-animations/Parcel.json';
import SelectContentModal from '../../plan/components/SelectContentModal';
import {
    archiveGroup,
    deleteMember,
    getMembers,
    getMemberTypes,
    unarchiveGroup,
    updateGroup,
    viewGroup,
} from '../api/groups.api';
import {
    CreateMemberModal,
    GroupMemberCountChip,
    GroupMemberRow,
    GroupSharingDrawer,
    IndividualInviteDialog,
    MemberImportDialog,
    MemberSelectionDialog,
} from '../components';
import ProfilePhotoUploadDialog from '../components/ProfilePhotoUploadDialog';
import TrackEventScraperDialog from '../components/TrackEventScraperDialog';
import TrackEventViewer from '../components/TrackEventViewer';
import AthleticNetScraperDialog from '../components/AthleticNetScraperDialog';
import BaseballRecruitScraperDialog from '../components/BaseballRecruitScraperDialog';
import BaseballCardGrid from '../components/baseball/BaseballCardGrid';
import BaseballStatsViewer from '../components/baseball/BaseballStatsViewer';
import { getProfiles, BaseballRecruitProfile } from 'api/BaseballRecruits';
import { ShareContentWithGroup } from './index';
import BulkMemberSelectionDialog from '../components/BulkMemberSelectionDialog';

const GroupDetails = () => {
    const [query, setQuery] = useQueryParams({
        'filter[name]': StringParam,
        'filter[managing_user_exists]': StringParam,
        page: NumberParam,
    });
    const [searchValue, setSearchValue] = useState<string>(query['filter[name]'] as string);
    const [openImportDialog, setOpenImportDialog] = useState<boolean>(false);
    const [openPhotoImportDialog, setOpenPhotoImportDialog] = useState<boolean>(false);
    const [deleting, setDeleting] = useState<false | string>(false);
    const [group, setGroup] = useState<Group>();
    const [members, setMembers] = useState<Member[]>();
    const [membersPaginationLinks, setMembersPaginationLinks] = useState<PaginationLink>();
    const [membersPaginationMeta, setMembersPaginationMeta] = useState<Meta>();
    const [selectedIndividual, setSelectedIndividual] = useState<Individual | null>(null);
    const [loading, setLoading] = useState(true);
    const [groupName, setGroupName] = useState('');
    const [nameValue, setNameValue] = useState('');
    const [coaches, setCoaches] = useState<Array<Member>>([]);
    const [selectedLogo, setSelectedLogo] = useState<File>();
    const [uploadLogo, setUploadLogo] = useState<File>();
    const [notifyUserMessage, setNotifyUserMessage] = useState('');
    const [imageUploading, setImageUploading] = useState(false);
    const [loadingMembers, setLoadingMembers] = useState(false);
    const [individualToInvite, setIndividualToInvite] = useState<Individual | null>(null);
    const [selectedMemberIndex, setSelectedMemberIndex] = useState<number | null>(null);
    const [memberTypes, setMemberTypes] = useState<Array<MemberType>>([]);
    const [createMemberModalOpen, toggleCreateMemberModalOpen] = useToggle(false);
    const [memberSelectionModal, toggleMemberSelectionModal] = useToggle(false);
    const debounceLoadData = useCallback(debounce(setQuery, 400), []); // eslint-disable-line react-hooks/exhaustive-deps
    const debounceUpdateName = useCallback(debounce(setNameValue, 400), []); // eslint-disable-line react-hooks/exhaustive-deps
    const [sharingOpen, toggleSharing] = useToggle();
    const [sessionsModalOpen, toggleSessionsModal] = useToggle();
    const [assessmentsModalOpen, toggleAssessmentsModal] = useToggle();
    const [attachDrawerOpen, toggleAttachDrawerOpen] = useToggle();
    const { push, location } = useHistory();
    const dispatch = useAppDispatch();
    const [bulkMemberSelectionOpen, setBulkMemberSelectionOpen] = useState(false);
    const [trackEventScraperOpen, setTrackEventScraperOpen] = useState(false);
    const [athleticNetMeetScraperOpen, setAthleticNetMeetScraperOpen] = useState(false);
    const [trackEventViewerOpen, setTrackEventViewerOpen] = useState(false);
    const [individualViewerOpen, setIndividualViewerOpen] = useState(false);
    const [individualViewerAthlete, setIndividualViewerAthlete] = useState<string>('');
    const [hasSuccessfulImports, setHasSuccessfulImports] = useState(false);
    const [athleticNetImportMember, setAthleticNetImportMember] = useState<Member | null>(null);
    const [athleticNetPickerOpen, setAthleticNetPickerOpen] = useState(false);
    const [trackViewMode, setTrackViewMode] = useState(false);
    const [rawPersonalRecords, setRawPersonalRecords] = useState<any[]>([]);
    const [personalRecords, setPersonalRecords] = useState<any[]>([]);
    const [loadingPRs, setLoadingPRs] = useState(false);

    // Baseball recruiting state
    const [baseballScraperOpen, setBaseballScraperOpen] = useState(false);
    const [baseballStatsViewerOpen, setBaseballStatsViewerOpen] = useState(false);
    const [baseballCardViewMode, setBaseballCardViewMode] = useState(false);
    const [recruitingAnchorEl, setRecruitingAnchorEl] = useState<HTMLElement | null>(null);
    const [recruitProfiles, setRecruitProfiles] = useState<BaseballRecruitProfile[]>([]);
    const [loadingRecruitProfiles, setLoadingRecruitProfiles] = useState(false);

    // Event matching state
    const [eventMappingOpen, setEventMappingOpen] = useState(false);
    const [customMappings, setCustomMappings] = useState<Record<string, string>>(() => {
        try {
            return JSON.parse(localStorage.getItem('track-event-custom-mappings') || '{}');
        } catch {
            return {};
        }
    });

    // Resolve event name: custom mapping takes priority over auto-normalization
    const resolveEventName = useCallback(
        (rawName: string): string => {
            if (customMappings[rawName]) return customMappings[rawName];
            return normalizeTrackEventName(rawName);
        },
        [customMappings],
    );

    // All unique raw event names (for the matching dialog)
    const rawEventNames = useMemo(
        () => [...new Set(rawPersonalRecords.map((pr) => pr.event_type))].sort(),
        [rawPersonalRecords],
    );

    // All unique auto-normalized names (stable dropdown options)
    const autoNormalizedNames = useMemo(
        () => [...new Set(rawEventNames.map(normalizeTrackEventName))].sort(),
        [rawEventNames],
    );

    // Event detail drill-down state
    const [eventDetailOpen, setEventDetailOpen] = useState(false);
    const [eventDetailEvent, setEventDetailEvent] = useState('');
    const [eventDetailPR, setEventDetailPR] = useState<any>(null);
    const [eventDetailData, setEventDetailData] = useState<any[]>([]);
    const [eventDetailLoading, setEventDetailLoading] = useState(false);
    const [eventDetailAthleteName, setEventDetailAthleteName] = useState('');

    // Dropdown menu anchors
    const [assignAnchorEl, setAssignAnchorEl] = useState<null | HTMLElement>(null);
    const [settingsAnchorEl, setSettingsAnchorEl] = useState<null | HTMLElement>(null);
    const [trackAnchorEl, setTrackAnchorEl] = useState<null | HTMLElement>(null);
    const [importAnchorEl, setImportAnchorEl] = useState<null | HTMLElement>(null);
    const [membersAnchorEl, setMembersAnchorEl] = useState<null | HTMLElement>(null);

    // Keyboard shortcuts for track event features
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // Check if user is typing in an input field
            if (
                event.target instanceof HTMLInputElement ||
                event.target instanceof HTMLTextAreaElement
            ) {
                return;
            }

            // Shift+Cmd+M or Shift+Ctrl+M - Open track event scraper dialog
            if (event.shiftKey && (event.metaKey || event.ctrlKey) && event.key === 'm') {
                event.preventDefault();
                setTrackEventScraperOpen(true);
            }

            // Shift+Cmd+D or Shift+Ctrl+D - Open track event viewer
            if (event.shiftKey && (event.metaKey || event.ctrlKey) && event.key === 'd') {
                event.preventDefault();
                setTrackEventViewerOpen(true);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    // Check if group has any track event data (from any scraper source)
    useEffect(() => {
        const checkTrackData = async () => {
            if (!group?.uuid) return;

            try {
                const response = await getPersonalRecords({
                    'filter.group': group.uuid,
                });
                const prs = response.data.personal_records || [];
                setHasSuccessfulImports(prs.length > 0);
            } catch {
                setHasSuccessfulImports(false);
            }
        };

        checkTrackData();
    }, [group?.uuid]);

    // Fetch recruit profiles when baseball card view mode is enabled or group is recruiting
    useEffect(() => {
        const fetchRecruitProfiles = async () => {
            if (!group?.uuid || !group?.is_recruiting) return;
            setLoadingRecruitProfiles(true);
            try {
                const response = await getProfiles(group.uuid);
                setRecruitProfiles(response.data.profiles || []);
            } catch {
                setRecruitProfiles([]);
            }
            setLoadingRecruitProfiles(false);
        };
        fetchRecruitProfiles();
    }, [group?.uuid, group?.is_recruiting]);

    const reloadRecruitProfiles = useCallback(async () => {
        if (!group?.uuid) return;
        try {
            const response = await getProfiles(group.uuid);
            setRecruitProfiles(response.data.profiles || []);
        } catch {
            // ignore
        }
    }, [group?.uuid]);

    // Fetch raw personal records when track view mode is enabled
    useEffect(() => {
        const fetchPRs = async () => {
            if (!trackViewMode || !group?.uuid) return;

            setLoadingPRs(true);
            try {
                const response = await getPersonalRecords({
                    'filter.group': group.uuid,
                });
                setRawPersonalRecords(response.data.personal_records || []);
            } catch (error) {
                console.error('Failed to fetch personal records:', error);
                setRawPersonalRecords([]);
            } finally {
                setLoadingPRs(false);
            }
        };

        fetchPRs();
    }, [trackViewMode, group?.uuid]);

    // Merge PRs using current mappings (re-runs when raw data or custom mappings change)
    useEffect(() => {
        if (rawPersonalRecords.length === 0) {
            setPersonalRecords([]);
            return;
        }

        const mergedMap = new Map<string, any>();

        // Convert meters to inches so field events are comparable
        const toIn = (v: number, u: string) => (u === 'm' ? v * 39.3701 : v);

        for (const pr of rawPersonalRecords) {
            const normalized = resolveEventName(pr.event_type);
            const key = `${pr.individual_uuid}|${normalized}`;
            const existing = mergedMap.get(key);
            // Normalize m → in on the stored PR so display is always feet/inches
            const normUnit = pr.result_unit === 'm' ? 'in' : pr.result_unit;
            const normResult =
                pr.result_unit === 'm'
                    ? (parseFloat(pr.best_result) * 39.3701).toFixed(2)
                    : pr.best_result;

            if (!existing) {
                mergedMap.set(key, {
                    ...pr,
                    event_type: normalized,
                    best_result: normResult,
                    result_unit: normUnit,
                    event_count: pr.event_count || 1,
                });
            } else {
                const isTimeBased = existing.result_unit === 's';
                const curr = toIn(parseFloat(existing.best_result), existing.result_unit);
                const next = toIn(parseFloat(pr.best_result), pr.result_unit);
                const isBetter = isTimeBased ? next < curr : next > curr;
                // When multiple raw event names normalize to one display event
                // (or multiple sources report the same meet), summing inflates
                // the tile count compared to the deduped drill-down modal.
                // Keep the highest known count rather than additive count.
                const mergedCount = Math.max(existing.event_count || 1, pr.event_count || 1);
                if (isBetter) {
                    mergedMap.set(key, {
                        ...pr,
                        event_type: normalized,
                        best_result: normResult,
                        result_unit: normUnit,
                        event_count: mergedCount,
                    });
                } else {
                    existing.event_count = mergedCount;
                }
            }
        }

        setPersonalRecords(Array.from(mergedMap.values()));
    }, [rawPersonalRecords, resolveEventName]);

    // Event detail drill-down helpers

    const handlePRTileClick = async (pr: any, athleteName: string) => {
        setEventDetailEvent(pr.event_type);
        setEventDetailPR(pr);
        setEventDetailAthleteName(athleteName);
        setEventDetailOpen(true);
        setEventDetailLoading(true);
        setEventDetailData([]);

        try {
            // Fetch all pages of results for this athlete
            let allResults: any[] = [];
            let currentPage = 1;
            let hasMore = true;

            while (hasMore) {
                const response = await getTrackEvents({
                    'filter.individual': pr.individual_uuid,
                    limit: 500,
                    page: currentPage,
                });
                allResults = [...allResults, ...response.data.data];
                hasMore = response.data.meta?.current_page < response.data.meta?.last_page;
                currentPage++;
            }

            const eventType = resolveEventName(pr.event_type);
            const filtered = allResults.filter(
                (perf: any) => resolveEventName(perf.event_type) === eventType,
            );

            // Deduplicate by date + normalized result (not meet name,
            // which can differ between sources for the same performance)
            const seen = new Set<string>();
            const deduped = filtered.filter((perf: any) => {
                const val =
                    perf.result_unit === 'm'
                        ? (parseFloat(perf.result_value) * 39.3701).toFixed(2)
                        : parseFloat(perf.result_value).toFixed(2);
                const d = perf.meet_date.split('T')[0];
                const key = `${d}|${val}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });

            deduped.sort(
                (a: any, b: any) =>
                    new Date(b.meet_date).getTime() - new Date(a.meet_date).getTime(),
            );
            setEventDetailData(deduped);
        } catch (error) {
            console.error('Failed to fetch event details:', error);
            setEventDetailData([]);
        } finally {
            setEventDetailLoading(false);
        }
    };

    const isPRRow = (perf: any) => {
        if (!eventDetailPR) return false;
        // Normalize m → in so PR highlight works across units
        const perfVal =
            perf.result_unit === 'm'
                ? parseFloat(perf.result_value) * 39.3701
                : parseFloat(perf.result_value);
        const prVal = parseFloat(eventDetailPR.best_result);
        return Math.abs(perfVal - prVal) < 0.1 && perf.meet_date === eventDetailPR.meet_date;
    };

    const getSourceLabel = (source: string | null) => {
        if (!source) return 'Manual';
        if (source.toLowerCase().includes('athletic')) return 'Athletic.net';
        if (source.toLowerCase().includes('mile')) return 'MileSplit';
        return source;
    };

    const getSourceColor = (source: string | null): 'primary' | 'secondary' | 'default' => {
        if (!source) return 'default';
        if (source.toLowerCase().includes('athletic')) return 'primary';
        if (source.toLowerCase().includes('mile')) return 'secondary';
        return 'default';
    };

    const handleSearchValueChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (group) {
            setLoadingMembers(true);
            setSearchValue(event.target.value);
            debounceLoadData({ 'filter[name]': event.target.value, page: 1 });
        }
    };

    const reloadGroupAfterUpdates = () => {
        if (!group) {
            return;
        }

        viewGroup(group.uuid)
            .then((groupResponse) => {
                setGroupName(groupResponse.data.name);
                setGroup({ ...groupResponse.data });
            })
            .catch(() => setNotifyUserMessage('Failed to load group'));
    };

    useEffect(() => {
        let isMounted = true;
        const uuid = getIdFromEndOfRoute(location.pathname);

        viewGroup(uuid)
            .then((groupResponse) => {
                if (isMounted) {
                    setGroupName(groupResponse.data.name);
                    setGroup({ ...groupResponse.data });
                }
            })
            .catch(() => isMounted && setNotifyUserMessage('Failed to load group'))
            .finally(() => isMounted && setLoading(false));

        getMemberTypes().then((types) => isMounted && setMemberTypes(types.data));

        return () => {
            isMounted = false;
        };
    }, [location.pathname]);

    const reloadMemberList = () => {
        if (!group) {
            return;
        }

        getMembers(group?.uuid, {
            include: 'individual.managingUser,memberType',
            limit: 50,
            ...query,
        })
            .then((response) => {
                setMembers(response.data.data);
                setMembersPaginationLinks(response.data.links);
                setMembersPaginationMeta(response.data.meta);
            })
            .catch(() => setNotifyUserMessage('Failed to load search results'));
    };

    useEffect(() => {
        let isMounted = true;
        const uuid = getIdFromEndOfRoute(location.pathname);

        getMembers(uuid, {
            include: 'individual.managingUser,memberType',
            limit: 50,
            ...query,
        })
            .then((response) => {
                if (isMounted) {
                    setMembers(response.data.data);
                    setMembersPaginationLinks(response.data.links);
                    setMembersPaginationMeta(response.data.meta);
                }
            })
            .catch(() => isMounted && setNotifyUserMessage('Failed to load search results'))
            .finally(() => isMounted && setLoadingMembers(false));

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, query]);

    useEffect(() => {
        let isMounted = true;

        if (group && nameValue && nameValue !== group.name) {
            updateGroup({
                ...group,
                name: nameValue,
            })
                .then(() => isMounted && setGroup({ ...group, name: nameValue }))
                .catch(() => {
                    if (isMounted) {
                        setNotifyUserMessage('Failed to update name of group.');
                        setGroupName(group.name);
                    }
                });
        }

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nameValue]);

    useEffect(() => {
        if (selectedMemberIndex !== null) {
            toggleCreateMemberModalOpen();
        }
    }, [selectedMemberIndex, toggleCreateMemberModalOpen]);

    const onCreateClose = (member: Member | false) => {
        toggleCreateMemberModalOpen();
        if (!member) {
            selectedMemberIndex !== null && setSelectedMemberIndex(null);
            return;
        }

        if (group) {
            if (selectedMemberIndex !== null) {
                const groupMembers = members ?? [];
                groupMembers[selectedMemberIndex] = member;
                setMembers(groupMembers);
                setSelectedMemberIndex(null);
            } else {
                setMembers([...(members ?? []), member]);
                setSelectedIndividual(null);
                reloadGroupAfterUpdates();
                dispatch(
                    pushMessage({
                        status: 'success',
                        message: 'Member successfully added to the group.',
                    }),
                );
            }

            member.memberType.name.toString() === MEMBER_TYPES.COACH &&
                setCoaches([...coaches, member]);
        }
    };

    const handleOnClose = async (file?: File) => {
        if (group) {
            setImageUploading(true);
            try {
                const response = await getSignedUrl();
                if (response.status === 201) {
                    const res = await uploadFile(response.data.url, file ?? uploadLogo);
                    if (res.status === 200) {
                        const upload = await updateGroup({ ...group, logo: response.data.key });
                        if (upload.status === 200) file && setUploadLogo(file);
                    }
                }
            } catch {
                setSelectedLogo(undefined);
                setNotifyUserMessage('Failed to upload the logo');
            } finally {
                setImageUploading(false);
            }
        }
    };

    const onUpdateNameOfGroup = (
        event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    ) => {
        if (group) {
            setGroupName(event.target.value);
            debounceUpdateName(event.target.value);
        }
    };

    const onEditMember = (index: number) => {
        if (group) {
            setSelectedMemberIndex(index);
        }
    };

    const onDeleteMember = (id: string) => {
        setDeleting(id);
        confirmViaDialog({
            confirmation: {
                title: 'Are You Sure?',
                message: 'Once you delete this member they are removed from this group',
                cancelButtonText: 'Cancel',
                okButtonText: 'Delete',
            },
        })
            .then((result) => {
                if (result && group) {
                    deleteMember(group.uuid, id)
                        .then(() => reloadMemberList())
                        .catch(() => {});
                }
            })
            .finally(() => {
                setDeleting(false);
            });
    };

    const onIndividualInvited = (individual: Individual) => {
        setIndividualToInvite(individual);
    };

    const onNextClicked = () => {
        const nextPage = membersPaginationMeta ? membersPaginationMeta.current_page + 1 : 1;
        handlePageChanged(nextPage);
    };

    const onPrevClicked = () => {
        const prevPage = membersPaginationMeta ? membersPaginationMeta.current_page - 1 : 1;
        handlePageChanged(prevPage);
    };

    const handlePageChanged = (newPage: number) => {
        setQuery((query) => ({ ...query, page: newPage }));
    };

    const ability = useContext(AbilityContext);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const primarySport = useSelector(primarySportSelector);
    const orgAdminOrgs = useSelector(getOrganizationsWithOrgAdminRole);
    const isOrgAdmin = (orgAdminOrgs?.length ?? 0) > 0;
    const isTrackFeaturesVisible = primarySport === 'track' || primarySport === 'multi_sport';
    const shouldPromptPrimarySport = !primarySport;

    const glassMenuStyle = {
        background: `linear-gradient(135deg, ${alpha(
            theme.palette.background.paper,
            0.7,
        )} 0%, ${alpha(theme.palette.background.paper, 0.5)} 100%)`,
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        borderRadius: '16px',
        border: `1px solid ${alpha(theme.palette.divider, 0.18)}`,
        boxShadow: `0 8px 32px 0 ${alpha(
            theme.palette.common.black,
            0.37,
        )}, inset 0 1px 0 0 ${alpha(theme.palette.common.white, 0.1)}`,
    };
    const actionRowSx = {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: { xs: 2, sm: 3 },
    };
    const actionButtonSx = {
        borderRadius: '24px',
        fontSize: { xs: '14px', sm: '15px' },
        padding: {
            xs: theme.spacing(3, 4),
            sm: theme.spacing(6, 8),
        },
        flex: { xs: '1 1 100%', sm: '0 0 auto' },
    };
    const mobileAwareMenuSx = {
        ...glassMenuStyle,
        maxHeight: isMobile ? '70vh' : undefined,
    };
    const heroGlassSurfaceSx = {
        background: `linear-gradient(135deg, ${alpha(
            theme.palette.background.paper,
            0.72,
        )} 0%, ${alpha(theme.palette.background.paper, 0.52)} 100%)`,
        backdropFilter: 'blur(18px) saturate(170%)',
        WebkitBackdropFilter: 'blur(18px) saturate(170%)',
        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        boxShadow: `0 10px 28px ${alpha(theme.palette.common.black, 0.14)}`,
        borderRadius: 3,
    };

    return (
        <TopBarWithShareIconLayout
            title={group?.name}
            onBack={() => push(ROUTES.ListOfGroups.path)}
            onShare={toggleSharing}
            showShareButton={group && ability.can('group:update-members', group)}
            mobileGlassUi
            scrollHeaderWithContent
        >
            {group && group.uuid && ability.can('group:update-members', group) && (
                <GroupSharingDrawer
                    groupUuid={group.uuid}
                    open={sharingOpen}
                    onClose={() => toggleSharing()}
                />
            )}
            <Box
                sx={{
                    width: '100%',
                    overflow: 'visible',
                    '@media (max-width: 600px)': {
                        backgroundColor: (theme) => theme.palette.backgroundColor.action,
                    },
                }}
            >
                <Box
                    sx={{
                        width: '90%',
                        margin: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        paddingBottom: (theme) => theme.spacing(20),

                        '@media (max-width:600px)': {
                            // equivalent to theme.breakpoints.down('sm')
                            width: '100%',
                            padding: (theme) => theme.spacing(0, 8, 20),
                        },
                    }}
                >
                    {shouldPromptPrimarySport && (
                        <Alert
                            severity="warning"
                            action={
                                <Button
                                    color="inherit"
                                    size="small"
                                    onClick={() => push('/profile')}
                                >
                                    Select now
                                </Button>
                            }
                            sx={{ mt: 2 }}
                        >
                            Set your primary sport to tailor this page and feature visibility.
                        </Alert>
                    )}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            width: '100%',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            margin: theme.spacing(8, 0),
                            marginTop: (theme) => theme.spacing(2),
                            [theme.breakpoints.down('md')]: {
                                display: 'grid',
                                gridTemplateColumns: '168px minmax(0, 1fr)',
                                alignItems: 'start',
                                columnGap: (theme) => theme.spacing(3),
                                rowGap: (theme) => theme.spacing(2),
                                margin: (theme) => theme.spacing(3, 0, 1),
                            },
                        }}
                    >
                        {group && (
                            <React.Fragment>
                                <Box
                                    sx={{
                                        [theme.breakpoints.down('md')]: {
                                            gridColumn: '1 / 2',
                                            width: '168px',
                                        },
                                    }}
                                >
                                    {imageUploading ? (
                                        <Skeleton
                                            sx={{
                                                width: '88px',
                                                height: '88px',
                                                borderRadius: '10%',
                                                marginRight: { xs: 0, md: '10px' },
                                            }}
                                        />
                                    ) : (
                                        <React.Fragment>
                                            <Can I="group:update" this={group}>
                                                <UploadLogo
                                                    uploadImage={selectedLogo ?? uploadLogo}
                                                    setUploadImage={setSelectedLogo}
                                                    onClose={handleOnClose}
                                                    button={false}
                                                    uploadContainerSx={{
                                                        width: '88px',
                                                        height: '88px',
                                                        borderRadius: '10%',
                                                        marginRight: { xs: 0, md: '10px' },
                                                    }}
                                                    uploadedImageSx={{
                                                        width: '88px',
                                                        height: '88px',
                                                        borderRadius: '10%',
                                                        marginRight: { xs: 0, md: '10px' },
                                                    }}
                                                    defaultImgSrc={group.image_urls.avatar || ''}
                                                />
                                            </Can>
                                            <Can not I="group:update" this={group}>
                                                <Avatar
                                                    alt={group.name}
                                                    src={group.image_urls.avatar || ''}
                                                    sx={{
                                                        width: '88px',
                                                        height: '88px',
                                                        borderRadius: '10%',
                                                        marginRight: { xs: 0, md: '10px' },
                                                    }}
                                                    variant={'rounded'}
                                                />
                                            </Can>
                                        </React.Fragment>
                                    )}
                                </Box>
                                <Box
                                    sx={{
                                        ...actionRowSx,
                                        width: '100%',
                                        minWidth: 0,
                                        p: { xs: 1.25, sm: 1.5 },
                                        ...heroGlassSurfaceSx,
                                        [theme.breakpoints.down('md')]: {
                                            gridColumn: '2 / 3',
                                        },
                                    }}
                                >
                                    <Can I="group:add-members" this={group}>
                                        {/* + Members Menu */}
                                        <CustomButton
                                            sx={actionButtonSx}
                                            variant="contained"
                                            color="primary"
                                            onClick={(e: React.MouseEvent<HTMLElement>) =>
                                                setMembersAnchorEl(e.currentTarget)
                                            }
                                            disabled={loading || !group}
                                        >
                                            + Members
                                            <KeyboardArrowDownIcon sx={{ ml: 1 }} />
                                        </CustomButton>
                                        <Menu
                                            anchorEl={membersAnchorEl}
                                            open={Boolean(membersAnchorEl)}
                                            onClose={() => setMembersAnchorEl(null)}
                                            disableScrollLock
                                            PaperProps={{
                                                sx: mobileAwareMenuSx,
                                            }}
                                        >
                                            <MenuItem
                                                onClick={() => {
                                                    setMembersAnchorEl(null);
                                                    toggleMemberSelectionModal();
                                                }}
                                            >
                                                Add Single Member
                                            </MenuItem>
                                            <MenuItem
                                                onClick={() => {
                                                    setMembersAnchorEl(null);
                                                    setBulkMemberSelectionOpen(true);
                                                }}
                                            >
                                                Add Many Members
                                            </MenuItem>
                                            <MenuItem
                                                onClick={() => {
                                                    setMembersAnchorEl(null);
                                                    setOpenImportDialog(true);
                                                }}
                                            >
                                                Import Roster
                                            </MenuItem>
                                            <MenuItem
                                                onClick={() => {
                                                    setMembersAnchorEl(null);
                                                    setOpenPhotoImportDialog(true);
                                                }}
                                            >
                                                Import Profile Photos
                                            </MenuItem>
                                            {group && ability.can('group:update', group) && (
                                                <MenuItem
                                                    onClick={() => {
                                                        setMembersAnchorEl(null);
                                                        toggleSessionsModal();
                                                    }}
                                                >
                                                    Assign Sessions
                                                </MenuItem>
                                            )}
                                            {group && ability.can('group:update', group) && (
                                                <MenuItem
                                                    onClick={() => {
                                                        setMembersAnchorEl(null);
                                                        toggleAssessmentsModal();
                                                    }}
                                                >
                                                    Assign Assessments
                                                </MenuItem>
                                            )}
                                            {group && ability.can('group:update', group) && (
                                                <MenuItem
                                                    onClick={() => {
                                                        setMembersAnchorEl(null);
                                                        toggleAttachDrawerOpen();
                                                    }}
                                                >
                                                    Share Content
                                                </MenuItem>
                                            )}
                                        </Menu>
                                    </Can>
                                    <Can I="group:add-members" this={group}>
                                        {!isMobile && (
                                            <Button
                                                variant={'outlined'}
                                                sx={actionButtonSx}
                                                color="primary"
                                                onClick={(e) => setImportAnchorEl(e.currentTarget)}
                                                endIcon={<KeyboardArrowDownIcon />}
                                            >
                                                Import
                                            </Button>
                                        )}
                                        <Menu
                                            anchorEl={importAnchorEl}
                                            open={Boolean(importAnchorEl)}
                                            onClose={() => setImportAnchorEl(null)}
                                            disableScrollLock
                                            PaperProps={{
                                                sx: mobileAwareMenuSx,
                                            }}
                                        >
                                            <MenuItem
                                                onClick={() => {
                                                    setImportAnchorEl(null);
                                                    setOpenImportDialog(true);
                                                }}
                                            >
                                                Import Roster
                                            </MenuItem>
                                            <MenuItem
                                                onClick={() => {
                                                    setImportAnchorEl(null);
                                                    setOpenPhotoImportDialog(true);
                                                }}
                                            >
                                                Import Profile Photos
                                            </MenuItem>
                                        </Menu>

                                        {isTrackFeaturesVisible && (
                                            <>
                                                {/* Track Features Menu */}
                                                <Button
                                                    variant={'outlined'}
                                                    sx={actionButtonSx}
                                                    color="primary"
                                                    onClick={(e) =>
                                                        setTrackAnchorEl(e.currentTarget)
                                                    }
                                                    endIcon={<KeyboardArrowDownIcon />}
                                                >
                                                    Track Features
                                                </Button>
                                                <Menu
                                                    anchorEl={trackAnchorEl}
                                                    open={Boolean(trackAnchorEl)}
                                                    onClose={() => setTrackAnchorEl(null)}
                                                    disableScrollLock
                                                    PaperProps={{
                                                        sx: mobileAwareMenuSx,
                                                    }}
                                                >
                                                    <MenuItem
                                                        onClick={() => {
                                                            setTrackAnchorEl(null);
                                                            setTrackEventScraperOpen(true);
                                                        }}
                                                    >
                                                        <img
                                                            src="/milesplit-logo.png"
                                                            alt="MileSplit"
                                                            style={{
                                                                height: 20,
                                                                objectFit: 'contain',
                                                                marginRight: 8,
                                                            }}
                                                        />
                                                        Meet Scraper
                                                    </MenuItem>
                                                    <MenuItem
                                                        onClick={() => {
                                                            setTrackAnchorEl(null);
                                                            setAthleticNetMeetScraperOpen(true);
                                                        }}
                                                    >
                                                        <img
                                                            src="/athletic-net-logo.png"
                                                            alt="Athletic.net"
                                                            style={{
                                                                height: 20,
                                                                objectFit: 'contain',
                                                                marginRight: 8,
                                                            }}
                                                        />
                                                        Meet Scraper
                                                    </MenuItem>
                                                    <MenuItem
                                                        onClick={() => {
                                                            setTrackAnchorEl(null);
                                                            setAthleticNetPickerOpen(true);
                                                        }}
                                                    >
                                                        <img
                                                            src="/athletic-net-logo.png"
                                                            alt="Athletic.net"
                                                            style={{
                                                                height: 20,
                                                                objectFit: 'contain',
                                                                marginRight: 8,
                                                            }}
                                                        />
                                                        Athlete Profile Scraper
                                                    </MenuItem>
                                                    {hasSuccessfulImports && (
                                                        <>
                                                            <MenuItem
                                                                onClick={() => {
                                                                    setTrackAnchorEl(null);
                                                                    setTrackEventViewerOpen(true);
                                                                }}
                                                            >
                                                                Meet Results
                                                            </MenuItem>
                                                            <MenuItem
                                                                onClick={() => {
                                                                    setTrackViewMode(
                                                                        !trackViewMode,
                                                                    );
                                                                }}
                                                                sx={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                }}
                                                            >
                                                                See Meet PRs Below
                                                                <Switch
                                                                    size="small"
                                                                    checked={trackViewMode}
                                                                    onChange={() =>
                                                                        setTrackViewMode(
                                                                            !trackViewMode,
                                                                        )
                                                                    }
                                                                    onClick={(e) =>
                                                                        e.stopPropagation()
                                                                    }
                                                                    sx={{ ml: 1 }}
                                                                />
                                                            </MenuItem>
                                                        </>
                                                    )}
                                                </Menu>
                                            </>
                                        )}

                                        {group?.is_recruiting && (
                                            <>
                                                <Button
                                                    variant={'outlined'}
                                                    sx={actionButtonSx}
                                                    color="primary"
                                                    onClick={(e) =>
                                                        setRecruitingAnchorEl(e.currentTarget)
                                                    }
                                                    endIcon={<KeyboardArrowDownIcon />}
                                                >
                                                    Recruiting Features
                                                </Button>
                                                <Menu
                                                    anchorEl={recruitingAnchorEl}
                                                    open={Boolean(recruitingAnchorEl)}
                                                    onClose={() => setRecruitingAnchorEl(null)}
                                                    disableScrollLock
                                                    PaperProps={{
                                                        sx: mobileAwareMenuSx,
                                                    }}
                                                >
                                                    <MenuItem
                                                        onClick={() => {
                                                            setRecruitingAnchorEl(null);
                                                            setBaseballScraperOpen(true);
                                                        }}
                                                    >
                                                        Roster and Stat Scrapers
                                                    </MenuItem>
                                                    {recruitProfiles.some(
                                                        (p) => p.scraped_data,
                                                    ) && (
                                                        <>
                                                            <MenuItem
                                                                onClick={() => {
                                                                    setRecruitingAnchorEl(null);
                                                                    setBaseballStatsViewerOpen(
                                                                        true,
                                                                    );
                                                                }}
                                                            >
                                                                View Stats
                                                            </MenuItem>
                                                            <MenuItem
                                                                onClick={() => {
                                                                    setBaseballCardViewMode(
                                                                        !baseballCardViewMode,
                                                                    );
                                                                }}
                                                                sx={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                }}
                                                            >
                                                                Baseball Cards View
                                                                <Switch
                                                                    size="small"
                                                                    checked={baseballCardViewMode}
                                                                    onChange={() =>
                                                                        setBaseballCardViewMode(
                                                                            !baseballCardViewMode,
                                                                        )
                                                                    }
                                                                    onClick={(e) =>
                                                                        e.stopPropagation()
                                                                    }
                                                                    sx={{ ml: 1 }}
                                                                />
                                                            </MenuItem>
                                                        </>
                                                    )}
                                                </Menu>
                                                <BaseballRecruitScraperDialog
                                                    open={baseballScraperOpen}
                                                    onClose={() => setBaseballScraperOpen(false)}
                                                    group={group}
                                                    profiles={recruitProfiles}
                                                    onSuccess={() => {
                                                        reloadRecruitProfiles();
                                                        reloadMemberList();
                                                    }}
                                                    onScrapingComplete={() => {
                                                        setBaseballScraperOpen(false);
                                                        setBaseballCardViewMode(true);
                                                        reloadRecruitProfiles();
                                                        reloadMemberList();
                                                    }}
                                                />
                                                {baseballStatsViewerOpen && (
                                                    <BaseballStatsViewer
                                                        open={baseballStatsViewerOpen}
                                                        onClose={() =>
                                                            setBaseballStatsViewerOpen(false)
                                                        }
                                                        profiles={recruitProfiles}
                                                        groupUuid={group.uuid}
                                                        groupName={group.name}
                                                        groupLogo={group.image_urls?.avatar || ''}
                                                        onRefresh={reloadRecruitProfiles}
                                                    />
                                                )}
                                            </>
                                        )}
                                        <MemberImportDialog
                                            open={openImportDialog}
                                            onClose={() => {
                                                setOpenImportDialog(false);
                                            }}
                                            group={group}
                                            onSuccess={() => {
                                                setOpenImportDialog(false);
                                                reloadMemberList();
                                                reloadGroupAfterUpdates();
                                            }}
                                        />
                                        <ProfilePhotoUploadDialog
                                            open={openPhotoImportDialog}
                                            onClose={() => {
                                                setOpenPhotoImportDialog(false);
                                            }}
                                            group={group}
                                            onSuccess={() => {
                                                setOpenPhotoImportDialog(false);
                                                reloadMemberList();
                                                reloadGroupAfterUpdates();
                                            }}
                                        />
                                        {group && (
                                            <>
                                                <TrackEventScraperDialog
                                                    open={trackEventScraperOpen}
                                                    onClose={() => setTrackEventScraperOpen(false)}
                                                    group={group}
                                                    onSuccess={() => {
                                                        setTrackEventScraperOpen(false);
                                                        setHasSuccessfulImports(true);
                                                        reloadMemberList();
                                                    }}
                                                    onViewResults={() =>
                                                        setTrackEventViewerOpen(true)
                                                    }
                                                />
                                                <TrackEventScraperDialog
                                                    open={athleticNetMeetScraperOpen}
                                                    onClose={() => {
                                                        setAthleticNetMeetScraperOpen(false);
                                                    }}
                                                    group={group}
                                                    defaultSource="athletic_net_meet"
                                                    onSuccess={() => {
                                                        setAthleticNetMeetScraperOpen(false);
                                                        setHasSuccessfulImports(true);
                                                        reloadMemberList();
                                                    }}
                                                    onViewResults={() =>
                                                        setTrackEventViewerOpen(true)
                                                    }
                                                />
                                                <TrackEventViewer
                                                    open={trackEventViewerOpen}
                                                    onClose={() => setTrackEventViewerOpen(false)}
                                                    group={group}
                                                    isOrgAdmin={isOrgAdmin}
                                                    members={members}
                                                />
                                                <TrackEventViewer
                                                    open={individualViewerOpen}
                                                    onClose={() => {
                                                        setIndividualViewerOpen(false);
                                                        setIndividualViewerAthlete('');
                                                    }}
                                                    group={group}
                                                    initialAthlete={individualViewerAthlete}
                                                    isOrgAdmin={isOrgAdmin}
                                                    members={members}
                                                />
                                            </>
                                        )}
                                    </Can>
                                    {group && ability.can('group:update', group) && !isMobile && (
                                        <Box sx={actionRowSx}>
                                            {/* Assign Menu */}
                                            <CustomButton
                                                sx={actionButtonSx}
                                                onClick={(e: React.MouseEvent<HTMLElement>) =>
                                                    setAssignAnchorEl(e.currentTarget)
                                                }
                                                variant="outlined"
                                                color="primary"
                                            >
                                                Assign
                                                <KeyboardArrowDownIcon sx={{ ml: 1 }} />
                                            </CustomButton>
                                            <Menu
                                                anchorEl={assignAnchorEl}
                                                open={Boolean(assignAnchorEl)}
                                                onClose={() => setAssignAnchorEl(null)}
                                                disableScrollLock
                                                PaperProps={{
                                                    sx: mobileAwareMenuSx,
                                                }}
                                            >
                                                <MenuItem
                                                    onClick={() => {
                                                        setAssignAnchorEl(null);
                                                        toggleSessionsModal();
                                                    }}
                                                >
                                                    Assign Sessions
                                                </MenuItem>
                                                <MenuItem
                                                    onClick={() => {
                                                        setAssignAnchorEl(null);
                                                        toggleAssessmentsModal();
                                                    }}
                                                >
                                                    Assign Assessments
                                                </MenuItem>
                                                <MenuItem
                                                    onClick={() => {
                                                        setAssignAnchorEl(null);
                                                        toggleAttachDrawerOpen();
                                                    }}
                                                >
                                                    Share Content
                                                </MenuItem>
                                            </Menu>
                                        </Box>
                                    )}
                                    {group && ability.can('group:update', group) && (
                                        <Box sx={actionRowSx}>
                                            <CustomButton
                                                sx={actionButtonSx}
                                                onClick={() => {
                                                    push(`${location.pathname}/speed-management`);
                                                }}
                                                variant="outlined"
                                                color="primary"
                                            >
                                                Speed Management
                                            </CustomButton>
                                        </Box>
                                    )}
                                    {group && ability.can('group:update', group) && (
                                        <Box sx={actionRowSx}>
                                            <CustomButton
                                                sx={actionButtonSx}
                                                onClick={() => {
                                                    push(`${location.pathname}/reports`);
                                                }}
                                                variant="outlined"
                                                color="primary"
                                            >
                                                Reports
                                            </CustomButton>
                                        </Box>
                                    )}
                                    {sessionsModalOpen && (
                                        <SelectContentModal
                                            filters={{
                                                'filter[organization]': group?.organization?.uuid,
                                            }}
                                            open={sessionsModalOpen}
                                            onClose={() => toggleSessionsModal()}
                                            onSelect={(sessions) => {
                                                const selectedSessionUuid =
                                                    Object.keys(sessions)[0];
                                                push(
                                                    `/plan/sessions/${selectedSessionUuid}/assign-group/${group?.uuid}`,
                                                );
                                            }}
                                            fullScreen={isMobile}
                                        />
                                    )}
                                    {assessmentsModalOpen && (
                                        <SelectContentModal
                                            filters={{
                                                'filter[organization]': group?.organization?.uuid,
                                            }}
                                            open={assessmentsModalOpen}
                                            onClose={() => toggleAssessmentsModal()}
                                            onSelect={(sessions) => {
                                                push(
                                                    `/class/assessments/${
                                                        Object.keys(sessions)[0]
                                                    }/assign-group/${group?.uuid}`,
                                                );
                                            }}
                                            fullScreen={isMobile}
                                            objectType={'form'}
                                        />
                                    )}
                                    <Drawer
                                        PaperProps={{
                                            style: { width: isMobile ? '100%' : '60%' },
                                        }}
                                        anchor={'right'}
                                        open={attachDrawerOpen}
                                        onBackdropClick={toggleAttachDrawerOpen}
                                    >
                                        <ShareContentWithGroup
                                            onClose={toggleAttachDrawerOpen}
                                            group={group}
                                        />
                                    </Drawer>
                                    <Box
                                        sx={{
                                            ...actionRowSx,
                                            mt: { xs: 0.5, sm: 0 },
                                        }}
                                    >
                                        {/* Settings Menu */}
                                        <Button
                                            variant={'outlined'}
                                            sx={actionButtonSx}
                                            color="primary"
                                            onClick={(e) => setSettingsAnchorEl(e.currentTarget)}
                                            endIcon={<KeyboardArrowDownIcon />}
                                        >
                                            Settings
                                        </Button>
                                        <Menu
                                            anchorEl={settingsAnchorEl}
                                            open={Boolean(settingsAnchorEl)}
                                            onClose={() => setSettingsAnchorEl(null)}
                                            disableScrollLock
                                            PaperProps={{
                                                sx: mobileAwareMenuSx,
                                            }}
                                        >
                                            <MenuItem
                                                onClick={() => {
                                                    setSettingsAnchorEl(null);
                                                    let newQuery = { ...query };
                                                    if (
                                                        query['filter[managing_user_exists]'] ===
                                                        'true'
                                                    ) {
                                                        newQuery['filter[managing_user_exists]'] =
                                                            undefined;
                                                    } else {
                                                        newQuery['filter[managing_user_exists]'] =
                                                            'true';
                                                    }
                                                    setQuery(newQuery);
                                                }}
                                            >
                                                {!query['filter[managing_user_exists]'] ? (
                                                    <VerifiedUserIcon
                                                        sx={{
                                                            mr: 1,
                                                            fontSize: '20px',
                                                            color: '#1B9CEA',
                                                        }}
                                                    />
                                                ) : null}
                                                {query['filter[managing_user_exists]'] === 'true'
                                                    ? 'See All Athletes'
                                                    : 'See Only Users'}
                                            </MenuItem>
                                            {group && ability.can('group:update', group) && (
                                                <>
                                                    <MenuItem
                                                        onClick={async () => {
                                                            if (!group?.uuid) return;
                                                            try {
                                                                const updated = {
                                                                    ...group,
                                                                    is_recruiting:
                                                                        !group.is_recruiting,
                                                                };
                                                                await updateGroup(updated);
                                                                setGroup(updated);
                                                            } catch {
                                                                setNotifyUserMessage(
                                                                    'Failed to update recruiting status',
                                                                );
                                                            }
                                                        }}
                                                    >
                                                        <Checkbox
                                                            checked={!!group?.is_recruiting}
                                                            size="small"
                                                            sx={{ mr: 1, p: 0 }}
                                                        />
                                                        Recruiting Group
                                                    </MenuItem>
                                                    {!group.archived_at && (
                                                        <MenuItem
                                                            onClick={async () => {
                                                                setSettingsAnchorEl(null);
                                                                setLoading(true);

                                                                if (!group?.uuid) {
                                                                    return;
                                                                }

                                                                try {
                                                                    await archiveGroup(group.uuid);
                                                                    push('/community/groups');
                                                                } catch (e) {
                                                                    setNotifyUserMessage(
                                                                        'Failed to archive group',
                                                                    );
                                                                }

                                                                setLoading(false);
                                                            }}
                                                            disabled={loading}
                                                        >
                                                            <Lottie
                                                                animationData={Archive}
                                                                loop={false}
                                                                autoplay={true}
                                                                style={{
                                                                    width: '20px',
                                                                    height: '20px',
                                                                    marginRight: '8px',
                                                                }}
                                                            />
                                                            Archive
                                                        </MenuItem>
                                                    )}
                                                    {group.archived_at && (
                                                        <MenuItem
                                                            onClick={async () => {
                                                                setSettingsAnchorEl(null);
                                                                setLoading(true);

                                                                if (!group?.uuid) {
                                                                    return;
                                                                }

                                                                try {
                                                                    await unarchiveGroup(
                                                                        group.uuid,
                                                                    );
                                                                    push('/community/groups');
                                                                } catch (e) {
                                                                    setNotifyUserMessage(
                                                                        'Failed to unarchive group',
                                                                    );
                                                                }

                                                                setLoading(false);
                                                            }}
                                                            disabled={loading}
                                                        >
                                                            Unarchive
                                                        </MenuItem>
                                                    )}
                                                </>
                                            )}
                                        </Menu>
                                    </Box>
                                </Box>
                                <Box
                                    sx={{
                                        width: { xs: '100%', sm: 'auto' },
                                        mt: { xs: 0.5, sm: 0 },
                                        [theme.breakpoints.down('md')]: {
                                            gridColumn: '1 / -1',
                                        },
                                    }}
                                >
                                    <GroupMemberCountChip
                                        group={group}
                                        glassUi
                                        sx={{
                                            minWidth: { xs: '100%', sm: 200 },
                                            py: { xs: 1.25, sm: 1.5 },
                                        }}
                                    />
                                </Box>
                            </React.Fragment>
                        )}
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'row', sm: 'column' },
                            alignItems: { xs: 'center', sm: 'stretch' },
                            gap: { xs: 2, sm: 0 },
                            mt: { xs: 2.5, sm: 0 },
                        }}
                    >
                        {loading ? (
                            <Skeleton
                                sx={{
                                    color: (theme) => theme.palette.text.primary,
                                    fontWeight: 600,
                                    fontSize: { xs: '24px', sm: '36px' },
                                    lineHeight: { xs: '30px', sm: '42px' },
                                    margin: {
                                        xs: 0,
                                        sm: theme.spacing(8, 0, 0),
                                    },
                                    width: { xs: '38%', sm: 'auto' },
                                    '&:before': {
                                        borderBottom: 'none',
                                    },
                                    '&:after': {
                                        borderBottom: 'none',
                                    },
                                }}
                            />
                        ) : (
                            <Input
                                onChange={onUpdateNameOfGroup}
                                disabled={!group}
                                value={groupName}
                                placeholder="Group Name"
                                sx={{
                                    color: (theme) => theme.palette.text.primary,
                                    fontWeight: 600,
                                    fontSize: { xs: '28px', sm: '36px' },
                                    lineHeight: { xs: '34px', sm: '42px' },
                                    margin: {
                                        xs: 0,
                                        sm: theme.spacing(8, 0, 0),
                                    },
                                    width: { xs: '38%', sm: 'auto' },
                                    minWidth: { xs: '120px', sm: 'auto' },
                                    '&:before': {
                                        borderBottom: 'none',
                                    },
                                    '&:after': {
                                        borderBottom: 'none',
                                    },
                                }}
                            />
                        )}

                        <Input
                            onChange={handleSearchValueChanged}
                            value={searchValue}
                            sx={{
                                width: { xs: '62%', sm: '100%' },
                                borderRadius: '32px',
                                padding: (theme) => theme.spacing(5),
                                background: (theme) => theme.palette.background.paper,
                                margin: {
                                    xs: 0,
                                    sm: theme.spacing(16, 0),
                                },
                                color: (theme) => theme.palette.text.secondary,
                                '&:before': {
                                    borderBottom: 'none',
                                },
                                '&:after': {
                                    borderBottom: 'none',
                                },
                                '&::placeholder': {
                                    color: (theme) => theme.palette.primary.main,
                                    marginLeft: (theme) => theme.spacing(6),
                                },
                                '& > input': {
                                    marginLeft: (theme) => theme.spacing(6),
                                },
                            }}
                            startAdornment={<SearchIcon />}
                            placeholder="Search"
                            endAdornment={loadingMembers && <CircularProgress size={30} />}
                            disabled={!group}
                        />
                    </Box>
                    {group?.organization && (
                        <ThumbnailTitleChip
                            title={group.organization.name}
                            src={group.organization.logo_url ?? ''}
                            avatarVariant="rounded"
                            sx={{
                                backgroundColor: '#ffffff',
                                display: 'flex',
                                width: 'auto',
                                gap: (theme) => theme.spacing(8),
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: (theme) => theme.spacing(8),
                                padding: (theme) => theme.spacing(8, 6),
                                borderRadius: 4,
                                boxShadow: (theme) => theme.shadows[1],
                                '& .title': {
                                    fontSize: 20,
                                    fontWeight: 500,
                                },
                                '& .thumbnail': {
                                    height: 44,
                                    width: 44,
                                },
                            }}
                        />
                    )}
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            background: theme.palette.backgroundColor.paper,
                            marginTop: 4,
                        }}
                    >
                        {coaches.length > 0 && (
                            <Box
                                sx={{
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        width: '100%',
                                        alignItems: 'center',
                                        padding: theme.spacing(8),
                                    }}
                                >
                                    <AvatarGroup max={3}>
                                        {coaches.map((coach, index) => (
                                            <AvatarWithInitials
                                                src={
                                                    coach.individual?.image_urls?.avatar ||
                                                    undefined
                                                }
                                                sx={{ width: '60px', height: '60px' }}
                                                key={index}
                                                firstname={coach.first_name}
                                                lastname={coach.last_name}
                                            />
                                        ))}
                                    </AvatarGroup>
                                    <Typography
                                        sx={{
                                            width: '50%',
                                            marginLeft: theme.spacing(8),
                                            fontWeight: 'normal',
                                            color: theme.palette.text.secondary,
                                            fontSize: '14px',
                                            lineHeight: '20px',

                                            [theme.breakpoints.down('sm')]: {
                                                width: 'auto',
                                            },
                                        }}
                                    >
                                        {coaches.map((coach, index) => (
                                            <span
                                                key={index}
                                                style={{
                                                    color: theme.palette.text.primary,
                                                    fontWeight: 700,
                                                }}
                                            >{`${coach.first_name ?? ''} ${
                                                coach.last_name ?? ''
                                            }, `}</span>
                                        ))}
                                        {coaches.length > 1 ? ' are coaches' : ' is coach'}
                                        {' of this group'}
                                    </Typography>
                                </Box>
                                <RowDivider />
                            </Box>
                        )}
                        {loading ? (
                            <Skeleton sx={{ width: '100%', height: '500px' }} />
                        ) : trackViewMode ? (
                            // Track PR View - Show PR Tiles grouped by athlete
                            loadingPRs ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <>
                                    {rawEventNames.length > 0 && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'flex-end',
                                                mb: 2,
                                            }}
                                        >
                                            <Tooltip title="Match events from different sources">
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<CompareArrowsIcon />}
                                                    onClick={() => setEventMappingOpen(true)}
                                                    sx={{
                                                        textTransform: 'none',
                                                        borderColor: alpha(
                                                            theme.palette.info.main,
                                                            0.4,
                                                        ),
                                                        color: theme.palette.info.main,
                                                        '&:hover': {
                                                            borderColor: theme.palette.info.main,
                                                            background: alpha(
                                                                theme.palette.info.main,
                                                                0.08,
                                                            ),
                                                        },
                                                    }}
                                                >
                                                    Match Events
                                                    {Object.keys(customMappings).length > 0 && (
                                                        <Chip
                                                            label={
                                                                Object.keys(customMappings).length
                                                            }
                                                            size="small"
                                                            color="info"
                                                            sx={{
                                                                ml: 1,
                                                                height: 20,
                                                                fontSize: '0.7rem',
                                                            }}
                                                        />
                                                    )}
                                                </Button>
                                            </Tooltip>
                                        </Box>
                                    )}
                                    <Grid container spacing={3}>
                                        {members?.map((member, memberIndex) => {
                                            const memberPRs = personalRecords.filter(
                                                (pr) =>
                                                    pr.individual_uuid === member.individual?.uuid,
                                            );

                                            if (memberPRs.length === 0) return null;

                                            return (
                                                <Grid item xs={12} md={6} lg={6} key={memberIndex}>
                                                    <Box
                                                        sx={{
                                                            background: alpha(
                                                                theme.palette.background.paper,
                                                                0.6,
                                                            ),
                                                            backdropFilter: 'blur(20px)',
                                                            borderRadius: 2,
                                                            border: `1px solid ${alpha(
                                                                theme.palette.divider,
                                                                0.1,
                                                            )}`,
                                                            boxShadow: `0 4px 16px ${alpha(
                                                                theme.palette.common.black,
                                                                0.05,
                                                            )}`,
                                                            p: 2,
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'center',
                                                                gap: 1.5,
                                                                mb: 2,
                                                            }}
                                                        >
                                                            <AvatarWithInitials
                                                                src={
                                                                    member.individual?.image_urls
                                                                        ?.avatar || undefined
                                                                }
                                                                sx={{ width: 50, height: 50 }}
                                                                firstname={member.first_name}
                                                                lastname={member.last_name}
                                                            />
                                                            <Typography
                                                                variant="h6"
                                                                fontWeight="700"
                                                            >
                                                                {member.first_name}{' '}
                                                                {member.last_name}
                                                            </Typography>
                                                            <Chip
                                                                label={`Type: ${
                                                                    member.memberType?.name ??
                                                                    'Unknown'
                                                                }`}
                                                                size="small"
                                                                color={
                                                                    member.memberType?.name ===
                                                                    'Athlete'
                                                                        ? 'default'
                                                                        : 'warning'
                                                                }
                                                                variant="outlined"
                                                                sx={{ fontSize: 11, height: 24 }}
                                                            />
                                                        </Box>
                                                        <Box
                                                            sx={{
                                                                display: 'grid',
                                                                gridTemplateColumns:
                                                                    'repeat(auto-fill, minmax(140px, 1fr))',
                                                                gap: 1.5,
                                                            }}
                                                        >
                                                            {memberPRs.map((pr, prIndex) => (
                                                                <Box
                                                                    key={prIndex}
                                                                    onClick={() =>
                                                                        handlePRTileClick(
                                                                            pr,
                                                                            `${member.first_name} ${member.last_name}`,
                                                                        )
                                                                    }
                                                                    sx={{
                                                                        cursor: 'pointer',
                                                                        background: alpha(
                                                                            theme.palette.background
                                                                                .paper,
                                                                            0.4,
                                                                        ),
                                                                        backdropFilter:
                                                                            'blur(20px)',
                                                                        borderRadius: 2,
                                                                        border: `1px solid ${alpha(
                                                                            theme.palette.info.main,
                                                                            0.3,
                                                                        )}`,
                                                                        boxShadow: `0 0 10px ${alpha(
                                                                            theme.palette.info.main,
                                                                            0.15,
                                                                        )}, 0 4px 16px ${alpha(
                                                                            theme.palette.common
                                                                                .black,
                                                                            0.05,
                                                                        )}`,
                                                                        p: 1.5,
                                                                        transition:
                                                                            'transform 0.2s, box-shadow 0.2s, border-color 0.2s',
                                                                        '&:hover': {
                                                                            transform:
                                                                                'translateY(-2px)',
                                                                            borderColor: alpha(
                                                                                theme.palette.info
                                                                                    .main,
                                                                                0.5,
                                                                            ),
                                                                            boxShadow: `0 0 15px ${alpha(
                                                                                theme.palette.info
                                                                                    .main,
                                                                                0.3,
                                                                            )}, 0 6px 24px ${alpha(
                                                                                theme.palette.common
                                                                                    .black,
                                                                                0.1,
                                                                            )}`,
                                                                        },
                                                                    }}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent:
                                                                                'space-between',
                                                                            mb: 1,
                                                                        }}
                                                                    >
                                                                        <Typography
                                                                            variant="caption"
                                                                            fontWeight="600"
                                                                            sx={{
                                                                                fontSize: '0.75rem',
                                                                            }}
                                                                        >
                                                                            {pr.event_type}
                                                                        </Typography>
                                                                        {pr.event_count > 1 && (
                                                                            <Chip
                                                                                label={`${pr.event_count}`}
                                                                                size="small"
                                                                                color="info"
                                                                                variant="outlined"
                                                                                sx={{
                                                                                    fontSize: 10,
                                                                                    height: 20,
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </Box>
                                                                    <Typography
                                                                        variant="h5"
                                                                        fontWeight="bold"
                                                                        sx={{
                                                                            color: theme.palette
                                                                                .info.main,
                                                                            mb: 0.5,
                                                                            letterSpacing:
                                                                                '-0.02em',
                                                                            fontSize: '1.5rem',
                                                                        }}
                                                                    >
                                                                        {formatTrackEventResult(
                                                                            pr.best_result,
                                                                            pr.result_unit,
                                                                        )}
                                                                    </Typography>
                                                                    {pr.meet_date && (
                                                                        <Typography
                                                                            variant="caption"
                                                                            color="text.secondary"
                                                                            display="block"
                                                                            sx={{
                                                                                fontSize: '0.7rem',
                                                                            }}
                                                                        >
                                                                            {new Date(
                                                                                pr.meet_date,
                                                                            ).toLocaleDateString()}
                                                                        </Typography>
                                                                    )}
                                                                    {pr.meet_name && (
                                                                        <Typography
                                                                            variant="caption"
                                                                            color="text.secondary"
                                                                            display="block"
                                                                            sx={{
                                                                                mt: 0.25,
                                                                                fontWeight: 500,
                                                                                fontSize: '0.65rem',
                                                                                overflow: 'hidden',
                                                                                textOverflow:
                                                                                    'ellipsis',
                                                                                whiteSpace:
                                                                                    'nowrap',
                                                                            }}
                                                                        >
                                                                            {pr.meet_name}
                                                                        </Typography>
                                                                    )}
                                                                    {pr.event_count > 1 &&
                                                                        (() => {
                                                                            const n =
                                                                                pr.event_count;
                                                                            const border = `1px solid ${alpha(
                                                                                theme.palette
                                                                                    .divider,
                                                                                0.15,
                                                                            )}`;
                                                                            const label = `See all ${n} meet time${
                                                                                n !== 1 ? 's' : ''
                                                                            }`;
                                                                            return (
                                                                                <Box
                                                                                    sx={{
                                                                                        display:
                                                                                            'flex',
                                                                                        alignItems:
                                                                                            'center',
                                                                                        justifyContent:
                                                                                            'flex-end',
                                                                                        mt: 1,
                                                                                        pt: 0.75,
                                                                                        borderTop:
                                                                                            border,
                                                                                    }}
                                                                                >
                                                                                    <Typography
                                                                                        variant="caption"
                                                                                        sx={{
                                                                                            color: theme
                                                                                                .palette
                                                                                                .info
                                                                                                .main,
                                                                                            fontWeight: 600,
                                                                                            fontSize:
                                                                                                '0.6rem',
                                                                                        }}
                                                                                    >
                                                                                        {label}
                                                                                    </Typography>
                                                                                    <ChevronRightIcon
                                                                                        sx={{
                                                                                            fontSize: 14,
                                                                                            color: theme
                                                                                                .palette
                                                                                                .info
                                                                                                .main,
                                                                                            ml: 0.25,
                                                                                        }}
                                                                                    />
                                                                                </Box>
                                                                            );
                                                                        })()}
                                                                </Box>
                                                            ))}
                                                            <Box
                                                                onClick={() => {
                                                                    setIndividualViewerAthlete(
                                                                        member.individual?.uuid ||
                                                                            '',
                                                                    );
                                                                    setIndividualViewerOpen(true);
                                                                }}
                                                                sx={{
                                                                    cursor: 'pointer',
                                                                    background: alpha(
                                                                        theme.palette.background
                                                                            .paper,
                                                                        0.25,
                                                                    ),
                                                                    backdropFilter: 'blur(24px)',
                                                                    WebkitBackdropFilter:
                                                                        'blur(24px)',
                                                                    borderRadius: 2,
                                                                    border: `1px solid ${alpha(
                                                                        theme.palette.primary.main,
                                                                        0.35,
                                                                    )}`,
                                                                    boxShadow: `0 0 12px ${alpha(
                                                                        theme.palette.primary.main,
                                                                        0.15,
                                                                    )}, 0 4px 16px ${alpha(
                                                                        theme.palette.common.black,
                                                                        0.06,
                                                                    )}`,
                                                                    p: 1.5,
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    minHeight: 100,
                                                                    transition:
                                                                        'transform 0.2s, box-shadow 0.2s, border-color 0.2s',
                                                                    '&:hover': {
                                                                        transform:
                                                                            'translateY(-2px)',
                                                                        borderColor: alpha(
                                                                            theme.palette.primary
                                                                                .main,
                                                                            0.6,
                                                                        ),
                                                                        boxShadow: `0 0 20px ${alpha(
                                                                            theme.palette.primary
                                                                                .main,
                                                                            0.3,
                                                                        )}, 0 6px 24px ${alpha(
                                                                            theme.palette.common
                                                                                .black,
                                                                            0.1,
                                                                        )}`,
                                                                    },
                                                                }}
                                                            >
                                                                <EmojiEventsIcon
                                                                    sx={{
                                                                        fontSize: 28,
                                                                        color: theme.palette.primary
                                                                            .main,
                                                                        mb: 0.75,
                                                                        opacity: 0.85,
                                                                    }}
                                                                />
                                                                <Typography
                                                                    variant="caption"
                                                                    fontWeight="700"
                                                                    sx={{
                                                                        color: theme.palette.primary
                                                                            .main,
                                                                        fontSize: '0.75rem',
                                                                        textAlign: 'center',
                                                                        lineHeight: 1.3,
                                                                    }}
                                                                >
                                                                    See All Meet Results
                                                                </Typography>
                                                                <ChevronRightIcon
                                                                    sx={{
                                                                        fontSize: 16,
                                                                        color: alpha(
                                                                            theme.palette.primary
                                                                                .main,
                                                                            0.6,
                                                                        ),
                                                                        mt: 0.5,
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                            );
                                        })}
                                        {personalRecords.length === 0 && (
                                            <Grid item xs={12}>
                                                <Box
                                                    sx={{
                                                        textAlign: 'center',
                                                        p: 4,
                                                        color: 'text.secondary',
                                                    }}
                                                >
                                                    <EmojiEventsIcon
                                                        sx={{ fontSize: 60, mb: 2, opacity: 0.3 }}
                                                    />
                                                    <Typography variant="h6" gutterBottom>
                                                        No Track Performances Yet
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        Import meet results to see personal records
                                                        here
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        )}
                                    </Grid>
                                </>
                            )
                        ) : baseballCardViewMode && recruitProfiles.length > 0 ? (
                            loadingRecruitProfiles ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <BaseballCardGrid
                                    profiles={recruitProfiles}
                                    onRefresh={reloadRecruitProfiles}
                                />
                            )
                        ) : (
                            // Regular Member List View
                            <Grid container spacing={3}>
                                {members?.map((member, index) => (
                                    <Grid item xs={12} md={6} lg={6} key={index}>
                                        <GroupMemberRow
                                            key={index}
                                            deleting={deleting ? deleting === member.uuid : false}
                                            group={group}
                                            member={member}
                                            onEditMember={() => onEditMember(index)}
                                            onDeleteMember={() => onDeleteMember(member.uuid)}
                                            onInviteClicked={onIndividualInvited}
                                            showAthleticNetImport={
                                                hasSuccessfulImports && isTrackFeaturesVisible
                                            }
                                            onAthleticNetImport={(m) =>
                                                setAthleticNetImportMember(m)
                                            }
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Box>
                    {members && (
                        <>
                            <NextPrevPagination
                                onNext={() => onNextClicked()}
                                onPrev={() => onPrevClicked()}
                                disablePrev={Boolean(!membersPaginationLinks?.prev)}
                                disableNext={Boolean(!membersPaginationLinks?.next)}
                            />
                            {isMobile && <div style={{ marginBottom: '40px' }}></div>}
                        </>
                    )}
                    <SnackBar
                        open={notifyUserMessage ? true : false}
                        setOpen={() => setNotifyUserMessage('')}
                        severity="error"
                        message={notifyUserMessage}
                    />
                    {group && (
                        <CreateMemberModal
                            group={group}
                            open={createMemberModalOpen}
                            onClose={onCreateClose}
                            memberTypes={memberTypes}
                            selectedIndividual={selectedIndividual}
                            editMember={
                                members && selectedMemberIndex !== null
                                    ? members[selectedMemberIndex]
                                    : null
                            }
                        />
                    )}
                </Box>
            </Box>
            {!!individualToInvite && (
                <IndividualInviteDialog
                    open={true}
                    onClose={() => setIndividualToInvite(null)}
                    individual={individualToInvite}
                />
            )}
            {group && memberSelectionModal && (
                <MemberSelectionDialog
                    open={memberSelectionModal}
                    organizationUuid={group?.organization?.uuid}
                    onCancel={() => {
                        toggleMemberSelectionModal();
                    }}
                    onIndividualSelected={(individual: Individual) => {
                        setSelectedIndividual(individual);
                        toggleMemberSelectionModal();
                        toggleCreateMemberModalOpen();
                    }}
                    onNewButtonpressed={() => {
                        toggleCreateMemberModalOpen();
                        toggleMemberSelectionModal();
                    }}
                    group={group}
                />
            )}
            {group && memberTypes.length > 0 && (
                <BulkMemberSelectionDialog
                    open={bulkMemberSelectionOpen}
                    onClose={() => setBulkMemberSelectionOpen(false)}
                    organizationUuid={group?.organization?.uuid}
                    group={group}
                    memberType={memberTypes[0]}
                    onSuccess={() => {
                        reloadMemberList();
                        reloadGroupAfterUpdates();
                    }}
                />
            )}
            {athleticNetImportMember?.individual && (
                <AthleticNetScraperDialog
                    open={true}
                    onClose={() => setAthleticNetImportMember(null)}
                    individual={athleticNetImportMember.individual}
                    onSuccess={() => {
                        setAthleticNetImportMember(null);
                        setHasSuccessfulImports(true);
                        reloadMemberList();
                        // Re-fetch PRs if in track view mode
                        if (trackViewMode && group?.uuid) {
                            getPersonalRecords({ 'filter.group': group.uuid })
                                .then((res) =>
                                    setRawPersonalRecords(res.data.personal_records || []),
                                )
                                .catch(() => {});
                        }
                    }}
                />
            )}

            {/* Athletic.net Athlete Picker Dialog */}
            <Dialog
                open={athleticNetPickerOpen}
                onClose={() => setAthleticNetPickerOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        background: alpha(theme.palette.background.paper, 0.9),
                        backdropFilter: 'blur(20px)',
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <img
                            src="/athletic-net-logo.png"
                            alt="Athletic.net"
                            style={{ height: 28, objectFit: 'contain' }}
                        />
                        <Box>
                            <Typography variant="h6" fontWeight={700}>
                                Import from Athletic.net
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Select an athlete to import their Athletic.net profile data
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={() => setAthleticNetPickerOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {!members || members.length === 0 ? (
                        <Alert severity="info">No members in this group yet.</Alert>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {members.map((member, idx) => (
                                <Box
                                    key={idx}
                                    onClick={() => {
                                        setAthleticNetPickerOpen(false);
                                        setAthleticNetImportMember(member);
                                    }}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        p: 1.5,
                                        borderRadius: 2,
                                        cursor: 'pointer',
                                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                        background: alpha(theme.palette.background.paper, 0.4),
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            background: alpha(theme.palette.info.main, 0.08),
                                            borderColor: alpha(theme.palette.info.main, 0.3),
                                            transform: 'translateX(4px)',
                                        },
                                    }}
                                >
                                    <AvatarWithInitials
                                        src={member.individual?.image_urls?.avatar || undefined}
                                        sx={{ width: 40, height: 40 }}
                                        firstname={member.first_name}
                                        lastname={member.last_name}
                                    />
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body1" fontWeight={600}>
                                            {member.first_name} {member.last_name}
                                        </Typography>
                                        {member.individual?.athletic_net_profile_url && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    display: 'block',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    maxWidth: 300,
                                                }}
                                            >
                                                {member.individual.athletic_net_profile_url}
                                            </Typography>
                                        )}
                                    </Box>
                                    {member.individual?.athletic_net_profile_url && (
                                        <Chip
                                            label="URL Set"
                                            size="small"
                                            color="success"
                                            variant="outlined"
                                        />
                                    )}
                                </Box>
                            ))}
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* Match Events Dialog */}
            <Dialog
                open={eventMappingOpen}
                onClose={() => setEventMappingOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        background: alpha(theme.palette.background.paper, 0.92),
                        backdropFilter: 'blur(20px)',
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CompareArrowsIcon color="info" />
                        <Box>
                            <Typography variant="h6" fontWeight={700}>
                                Match Events
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Events from different sources may have different names. Adjust the
                                mappings below to combine them into one tile.
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={() => setEventMappingOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {rawEventNames.length === 0 ? (
                        <Alert severity="info">No events found to match.</Alert>
                    ) : (
                        <>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>
                                                Original Event Name (from scraper)
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>
                                                Matched To
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rawEventNames.map((rawName) => {
                                            const currentResolved = resolveEventName(rawName);
                                            const autoResolved = normalizeTrackEventName(rawName);
                                            const isCustom = !!customMappings[rawName];

                                            return (
                                                <TableRow
                                                    key={rawName}
                                                    sx={
                                                        isCustom
                                                            ? {
                                                                  background: alpha(
                                                                      theme.palette.info.main,
                                                                      0.05,
                                                                  ),
                                                              }
                                                            : {}
                                                    }
                                                >
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {rawName}
                                                        </Typography>
                                                        {autoResolved !== rawName && (
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                            >
                                                                Auto: {autoResolved}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Select
                                                            size="small"
                                                            value={currentResolved}
                                                            onChange={(e) => {
                                                                const target = e.target
                                                                    .value as string;
                                                                const updated = {
                                                                    ...customMappings,
                                                                };
                                                                if (target === autoResolved) {
                                                                    delete updated[rawName];
                                                                } else {
                                                                    updated[rawName] = target;
                                                                }
                                                                setCustomMappings(updated);
                                                                localStorage.setItem(
                                                                    'track-event-custom-mappings',
                                                                    JSON.stringify(updated),
                                                                );
                                                            }}
                                                            fullWidth
                                                            sx={{ minWidth: 200 }}
                                                        >
                                                            {autoNormalizedNames.map((name) => (
                                                                <MenuItem key={name} value={name}>
                                                                    {name}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {Object.keys(customMappings).length > 0 && (
                                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        size="small"
                                        color="warning"
                                        onClick={() => {
                                            setCustomMappings({});
                                            localStorage.removeItem('track-event-custom-mappings');
                                        }}
                                    >
                                        Reset All Custom Mappings
                                    </Button>
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Event Detail Drill-Down Dialog */}
            <Dialog
                open={eventDetailOpen}
                onClose={() => setEventDetailOpen(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        background: alpha(theme.palette.background.paper, 0.85),
                        backdropFilter: 'blur(20px)',
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        pb: 1,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton size="small" onClick={() => setEventDetailOpen(false)}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Box>
                            <Typography variant="h6" fontWeight={700}>
                                {eventDetailEvent}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {eventDetailAthleteName} &middot; {eventDetailData.length}{' '}
                                performance
                                {eventDetailData.length !== 1 ? 's' : ''}
                            </Typography>
                        </Box>
                        {eventDetailPR && (
                            <Chip
                                icon={<EmojiEventsIcon />}
                                label={`PR: ${formatTrackEventResult(
                                    eventDetailPR.best_result,
                                    eventDetailPR.result_unit,
                                )}`}
                                color="success"
                                size="small"
                                sx={{ ml: 1, fontWeight: 600 }}
                            />
                        )}
                    </Box>
                    <IconButton onClick={() => setEventDetailOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {eventDetailLoading ? (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                py: 4,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : eventDetailData.length === 0 ? (
                        <Alert severity="info">No performances found for this event.</Alert>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Meet</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                                            Result
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Source</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {eventDetailData.map((perf: any) => {
                                        const isHighlighted = isPRRow(perf);
                                        return (
                                            <TableRow
                                                key={perf.uuid}
                                                sx={{
                                                    ...(isHighlighted
                                                        ? {
                                                              borderLeft: `4px solid ${theme.palette.success.main}`,
                                                              background: alpha(
                                                                  theme.palette.success.main,
                                                                  0.08,
                                                              ),
                                                          }
                                                        : {}),
                                                    '&:hover': {
                                                        background: alpha(
                                                            theme.palette.action.hover,
                                                            0.04,
                                                        ),
                                                    },
                                                }}
                                            >
                                                <TableCell>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.5,
                                                        }}
                                                    >
                                                        {isHighlighted && (
                                                            <EmojiEventsIcon
                                                                sx={{
                                                                    fontSize: 16,
                                                                    color: theme.palette.success
                                                                        .main,
                                                                }}
                                                            />
                                                        )}
                                                        <Typography variant="body2">
                                                            {new Date(
                                                                perf.meet_date,
                                                            ).toLocaleDateString('en-US', {
                                                                year: 'numeric',
                                                                month: 'short',
                                                                day: 'numeric',
                                                            })}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {perf.meet_name || '\u2014'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {perf.meet_location || '\u2014'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={isHighlighted ? 700 : 400}
                                                        sx={
                                                            isHighlighted
                                                                ? {
                                                                      color: theme.palette.success
                                                                          .main,
                                                                  }
                                                                : {}
                                                        }
                                                    >
                                                        {formatTrackEventResult(
                                                            perf.result_value,
                                                            perf.result_unit,
                                                        )}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {perf.import_source &&
                                                    perf.import_source
                                                        .toLowerCase()
                                                        .includes('mile') ? (
                                                        <img
                                                            src="/milesplit-logo.png"
                                                            alt="MileSplit"
                                                            style={{
                                                                height: 28,
                                                                objectFit: 'contain',
                                                            }}
                                                        />
                                                    ) : perf.import_source &&
                                                      perf.import_source
                                                          .toLowerCase()
                                                          .includes('athletic') ? (
                                                        <img
                                                            src="/athletic-net-logo.png"
                                                            alt="Athletic.net"
                                                            style={{
                                                                height: 28,
                                                                objectFit: 'contain',
                                                            }}
                                                        />
                                                    ) : (
                                                        <Chip
                                                            label={getSourceLabel(
                                                                perf.import_source,
                                                            )}
                                                            color={getSourceColor(
                                                                perf.import_source,
                                                            )}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
            </Dialog>
        </TopBarWithShareIconLayout>
    );
};

export default GroupDetails;
// trigger rebuild
